import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { products } from '@/data/mockData';
import { useCart } from '@/context/CartContext';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const Skeleton = () => (
  <div className="border-2 border-black animate-pulse">
    <div className="aspect-[4/5] bg-gray-100" />
    <div className="p-6 space-y-4">
      <div className="h-6 bg-gray-100 w-3/4" />
      <div className="h-4 bg-gray-100 w-1/2" />
      <div className="h-10 bg-gray-100 w-full" />
    </div>
  </div>
);

const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    setIsAdding(true);
    setTimeout(() => {
      addToCart(product);
      setIsAdding(false);
    }, 600);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ delay: (index % 3) * 0.1 }}
      className="group relative border-2 border-black flex flex-col bg-white overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-50">
        <img src={product.image} alt={product.name} className="absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:opacity-0" />
        <img src={product.hoverImage} alt={`${product.name} alternate`} className="absolute inset-0 w-full h-full object-cover opacity-0 transition-all duration-700 group-hover:opacity-100" />
      </div>
      <div className="p-6 flex flex-col gap-4 border-t-2 border-black bg-white">
        <div className="flex justify-between items-start">
          <h3 className="font-black text-2xl tracking-tighter uppercase leading-none">{product.name}</h3>
          <p className="text-xl font-black tracking-tighter">${product.price}</p>
        </div>
        <button 
          onClick={handleAdd}
          disabled={isAdding}
          className="w-full py-3 border-2 border-black font-black text-xs tracking-widest uppercase hover:bg-black hover:text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isAdding ? 'ADDING TO BAG...' : 'ADD TO CART'}
          {!isAdding && <SafeIcon icon={FiIcons.FiPlus} />}
        </button>
      </div>
    </motion.div>
  );
};

const ProductGallery = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="drops" className="py-32 px-6 max-w-[1600px] mx-auto min-h-screen">
      <div className="mb-20">
        <h2 className="text-6xl md:text-8xl font-black tracking-tighter uppercase leading-none mb-6">Latest Drop.</h2>
        <p className="text-lg text-gray-600 font-medium border-l-4 border-black pl-6 max-w-xl">
          Architectural optics for the modern visionary. Experience the spectrum in our latest seasonal workspace.
        </p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {loading 
          ? [...Array(6)].map((_, i) => <Skeleton key={i} />)
          : products.map((product, idx) => <ProductCard key={product.id} product={product} index={idx} />)
        }
      </div>
    </section>
  );
};

export default ProductGallery;